import { useMemo, useRef, useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { SearchableMultiSelect } from '@/components/ui/searchable-multi-select'
import { RepositoryAssetThumbnail } from '@/components/RepositoryAssetThumbnail'
import { ContentDetailModal } from '@/components/ContentDetailModal'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { ChevronDown } from 'lucide-react'
import { Search, Upload, Download, Pencil, Trash2, Plus, Settings2 } from 'lucide-react'
import {
  useMarketingTagCategories,
  useMarketingAssets,
  useUploadContent,
  useCreateMarketingAsset,
  useUpdateMarketingAsset,
  useDeleteMarketingAsset,
  useCreateMarketingTagValue,
  useDeleteMarketingTagValue,
} from '@/hooks/use-api'
import { apiClient } from '@/lib/api'
import { useUser } from '@/contexts/UserContext'
import { useToast } from '@/hooks/use-toast'
import type { MarketingAsset, MarketingTagCategory } from '@/types'

function formatBytes(n?: number | null): string {
  if (!n) return ''
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

/** Selection map keyed by category → selected tag-value ids. */
type Selection = Record<string, string[]>

export function MarketingPanel({ columns = 3 }: { columns?: number }) {
  const { isAdmin, userType, currentUser } = useUser()
  const canManage = isAdmin || userType === 'Marketing'
  const { toast } = useToast()

  const { data: categories = [] } = useMarketingTagCategories()
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<Selection>({})
  const [sort, setSort] = useState<'recent' | 'downloads'>('recent')
  const [page, setPage] = useState(1)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [editing, setEditing] = useState<MarketingAsset | null>(null)
  const [deleting, setDeleting] = useState<MarketingAsset | null>(null)
  const [manageOpen, setManageOpen] = useState(false)
  const [previewId, setPreviewId] = useState<string | null>(null)
  const [filtersOpen, setFiltersOpen] = useState(false)

  const limit = 24
  const tagValueIds = useMemo(() => Object.values(filters).flat(), [filters])
  const { data, isLoading, error } = useMarketingAssets({
    q: search.trim() || undefined,
    tagValueIds: tagValueIds.length ? tagValueIds : undefined,
    sort,
    page,
    limit,
  })
  const assets = data?.data ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / limit))

  const deleteAsset = useDeleteMarketingAsset()
  const downloadAsset = async (a: MarketingAsset) => {
    try {
      const blob = await apiClient.downloadContent(a.id)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = a.title
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch {
      toast({ title: 'Download failed', variant: 'destructive' })
    }
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <Card>
        <CardContent className="space-y-3 pt-6">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-56 flex-1">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                aria-label="Search marketing assets"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value)
                  setPage(1)
                }}
                placeholder="Search by name, description, or tag…"
                className="pl-9"
              />
            </div>
            <Select value={sort} onValueChange={(v) => setSort(v as 'recent' | 'downloads')}>
              <SelectTrigger className="w-44" aria-label="Sort assets">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">Most Recent</SelectItem>
                <SelectItem value="downloads">Most Downloaded</SelectItem>
              </SelectContent>
            </Select>
            {canManage && (
              <>
                <Button variant="outline" onClick={() => setManageOpen(true)}>
                  <Settings2 className="mr-2 h-4 w-4" aria-hidden="true" />
                  Manage tags
                </Button>
                <Button onClick={() => setUploadOpen(true)}>
                  <Upload className="mr-2 h-4 w-4" aria-hidden="true" />
                  Upload
                </Button>
              </>
            )}
          </div>
          {/* Tag filters — collapsed by default so the view is clean on load. */}
          <Collapsible open={filtersOpen} onOpenChange={setFiltersOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-1">
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${filtersOpen ? 'rotate-180' : ''}`}
                  aria-hidden="true"
                />
                Filters
                {tagValueIds.length > 0 && (
                  <Badge variant="secondary" className="ml-1">
                    {tagValueIds.length}
                  </Badge>
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-3">
              <div className="flex flex-wrap gap-2">
                {categories.map((c) => (
                  <SearchableMultiSelect
                    key={c.key}
                    label={c.label}
                    options={c.values.map((v) => ({ value: v.id, label: v.value }))}
                    selected={filters[c.key] ?? []}
                    onChange={(next) => {
                      setFilters((f) => ({ ...f, [c.key]: next }))
                      setPage(1)
                    }}
                  />
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>

      {/* Results */}
      {isLoading && (
        <p role="status" aria-live="polite" className="py-12 text-center text-muted-foreground">
          Loading…
        </p>
      )}
      {error && (
        <p role="alert" className="py-12 text-center text-destructive">
          Failed to load assets.
        </p>
      )}
      {!isLoading && !error && assets.length === 0 && (
        <p className="py-12 text-center text-muted-foreground">No assets match your filters.</p>
      )}

      <div
        className="grid gap-4"
        style={{
          gridTemplateColumns: `repeat(${Math.min(6, Math.max(1, Math.round(columns) || 3))}, minmax(0, 1fr))`,
        }}
      >
        {assets.map((a) => {
          const canEdit = canManage && (isAdmin || a.owner_id === currentUser?.id)
          return (
            <Card key={a.id} className="flex flex-col overflow-hidden">
              <button
                type="button"
                onClick={() => setPreviewId(a.id)}
                className="block w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={`Preview ${a.title}`}
              >
                <RepositoryAssetThumbnail
                  contentVersionId={a.id}
                  fileType={a.file_type}
                  hasThumbnail={!!a.thumbnail_url}
                  title={a.title}
                  size="card"
                />
              </button>
              <CardContent className="flex flex-1 flex-col gap-2 pt-4">
                <div className="flex items-start justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => setPreviewId(a.id)}
                    className="text-left font-medium leading-tight hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {a.title}
                  </button>
                  <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                    <Download className="h-3 w-3" aria-hidden="true" />
                    {a.download_count}
                  </span>
                </div>
                {a.description && (
                  <p className="line-clamp-2 text-sm text-muted-foreground">{a.description}</p>
                )}
                {a.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {a.tags.slice(0, 6).map((t) => (
                      <Badge key={t.id} variant="secondary" className="text-[11px]">
                        {t.value}
                      </Badge>
                    ))}
                    {a.tags.length > 6 && (
                      <span className="text-[11px] text-muted-foreground">
                        +{a.tags.length - 6}
                      </span>
                    )}
                  </div>
                )}
                <div className="mt-auto flex items-center justify-between pt-2 text-xs text-muted-foreground">
                  <span>
                    {a.file_type} · {formatBytes(a.content_size)}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => downloadAsset(a)}>
                      <Download className="mr-1 h-4 w-4" aria-hidden="true" />
                      Download
                    </Button>
                    {canEdit && (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditing(a)}
                          aria-label={`Edit ${a.title}`}
                        >
                          <Pencil className="h-4 w-4" aria-hidden="true" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive"
                          onClick={() => setDeleting(a)}
                          aria-label={`Remove ${a.title}`}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            {total} asset{total === 1 ? '' : 's'} · page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {uploadOpen && (
        <MarketingAssetDialog
          categories={categories}
          canManageTags={canManage}
          onClose={() => setUploadOpen(false)}
        />
      )}
      {editing && (
        <MarketingAssetDialog
          categories={categories}
          canManageTags={canManage}
          asset={editing}
          onClose={() => setEditing(null)}
        />
      )}
      {manageOpen && (
        <ManageTagsDialog categories={categories} onClose={() => setManageOpen(false)} />
      )}

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this asset?</AlertDialogTitle>
            <AlertDialogDescription>
              “{deleting?.title}” will be removed from the Marketing library. The underlying file is
              kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleting)
                  deleteAsset.mutate(deleting.id, {
                    onSuccess: () => toast({ title: 'Asset removed' }),
                  })
                setDeleting(null)
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* In-browser preview (PDF / image / text) — opened on asset click. */}
      <ContentDetailModal
        contentId={previewId}
        open={!!previewId}
        onOpenChange={(o) => {
          if (!o) setPreviewId(null)
        }}
      />
    </div>
  )
}

// ----- Upload / edit dialog --------------------------------------------------
function MarketingAssetDialog({
  categories,
  canManageTags,
  asset,
  onClose,
}: {
  categories: MarketingTagCategory[]
  canManageTags: boolean
  asset?: MarketingAsset
  onClose: () => void
}) {
  const { toast } = useToast()
  const isEdit = !!asset
  const upload = useUploadContent()
  const create = useCreateMarketingAsset()
  const update = useUpdateMarketingAsset()
  const addValue = useCreateMarketingTagValue()

  const fileRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [title, setTitle] = useState(asset?.title ?? '')
  const [description, setDescription] = useState(asset?.description ?? '')
  const [selection, setSelection] = useState<Selection>(() => {
    const s: Selection = {}
    for (const t of asset?.tags ?? []) {
      const arr = s[t.category_key] ?? (s[t.category_key] = [])
      arr.push(t.id)
    }
    return s
  })
  const [submitting, setSubmitting] = useState(false)

  const selectedIds = useMemo(() => Object.values(selection).flat(), [selection])

  const onFile = (f: File | null) => {
    setFile(f)
    if (f && !title.trim()) setTitle(f.name)
  }

  const addNewValue = async (categoryKey: string, value: string) => {
    try {
      const created = await addValue.mutateAsync({ category_key: categoryKey, value })
      setSelection((s) => ({ ...s, [categoryKey]: [...(s[categoryKey] ?? []), created.id] }))
    } catch (e) {
      toast({
        title: 'Could not add value',
        description:
          (e as { response?: { data?: { error?: string } } })?.response?.data?.error || '',
        variant: 'destructive',
      })
    }
  }

  const submit = async () => {
    if (!title.trim()) {
      toast({ title: 'Title is required', variant: 'destructive' })
      return
    }
    if (selectedIds.length === 0) {
      toast({ title: 'Select at least one tag', variant: 'destructive' })
      return
    }
    if (!isEdit && !file) {
      toast({ title: 'Choose a file to upload', variant: 'destructive' })
      return
    }
    setSubmitting(true)
    try {
      if (isEdit) {
        await update.mutateAsync({
          id: asset!.id,
          payload: {
            title: title.trim(),
            description: description.trim() || null,
            tagValueIds: selectedIds,
          },
        })
        toast({ title: 'Asset updated' })
      } else {
        const fd = new FormData()
        fd.append('file', file!)
        fd.append('title', title.trim())
        if (description.trim()) fd.append('description', description.trim())
        const uploaded = await upload.mutateAsync(fd)
        await create.mutateAsync({
          contentVersionId: uploaded.data.id,
          title: title.trim(),
          description: description.trim() || undefined,
          tagValueIds: selectedIds,
        })
        toast({ title: 'Asset uploaded' })
      }
      onClose()
    } catch (e) {
      toast({
        title: isEdit ? 'Update failed' : 'Upload failed',
        description:
          (e as { response?: { data?: { error?: string } } })?.response?.data?.error || '',
        variant: 'destructive',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit asset' : 'Upload marketing asset'}</DialogTitle>
        </DialogHeader>
        <div className="flex-1 space-y-4 overflow-y-auto pr-1">
          {!isEdit && (
            <div>
              <label htmlFor="mkt-file" className="mb-1 block text-sm font-medium">
                File *
              </label>
              <input
                id="mkt-file"
                ref={fileRef}
                type="file"
                className="sr-only"
                aria-label="Choose a file to upload"
                onChange={(e) => onFile(e.target.files?.[0] ?? null)}
              />
              <Button
                variant="outline"
                onClick={() => fileRef.current?.click()}
                aria-label={
                  file
                    ? `Selected file: ${file.name}. Choose a different file`
                    : 'Choose file to upload'
                }
              >
                {file ? file.name : 'Choose file…'}
              </Button>
              {file && (
                <span className="ml-2 text-xs text-muted-foreground">{formatBytes(file.size)}</span>
              )}
            </div>
          )}
          <div>
            <label htmlFor="mkt-title" className="mb-1 block text-sm font-medium">
              Title *
            </label>
            <Input id="mkt-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div>
            <label htmlFor="mkt-desc" className="mb-1 block text-sm font-medium">
              Description
            </label>
            <textarea
              id="mkt-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div>
            <p className="mb-1 text-sm font-medium">
              Tags <span className="text-muted-foreground">(at least one required)</span>
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {categories.map((c) => (
                <TagCategoryField
                  key={c.key}
                  category={c}
                  selected={selection[c.key] ?? []}
                  onChange={(next) => setSelection((s) => ({ ...s, [c.key]: next }))}
                  onAddValue={canManageTags ? (v) => addNewValue(c.key, v) : undefined}
                />
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting ? 'Saving…' : isEdit ? 'Save' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// One category's multiselect + optional inline "add value".
function TagCategoryField({
  category,
  selected,
  onChange,
  onAddValue,
}: {
  category: MarketingTagCategory
  selected: string[]
  onChange: (next: string[]) => void
  onAddValue?: (value: string) => void
}) {
  const [adding, setAdding] = useState(false)
  const [newValue, setNewValue] = useState('')
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">{category.label}</span>
        {onAddValue && (
          <button
            type="button"
            className="flex items-center gap-0.5 text-xs text-primary hover:underline"
            onClick={() => setAdding((a) => !a)}
          >
            <Plus className="h-3 w-3" aria-hidden="true" />
            New
          </button>
        )}
      </div>
      <SearchableMultiSelect
        label={category.label}
        options={category.values.map((v) => ({ value: v.id, label: v.value }))}
        selected={selected}
        onChange={onChange}
        triggerClassName="w-full"
      />
      {adding && onAddValue && (
        <div className="mt-1 flex gap-1">
          <Input
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            placeholder="New value…"
            className="h-8 text-sm"
            aria-label={`New ${category.label} value`}
          />
          <Button
            size="sm"
            className="h-8"
            onClick={() => {
              if (newValue.trim()) {
                onAddValue(newValue.trim())
                setNewValue('')
                setAdding(false)
              }
            }}
          >
            Add
          </Button>
        </div>
      )}
    </div>
  )
}

// ----- Manage (delete) tag values --------------------------------------------
function ManageTagsDialog({
  categories,
  onClose,
}: {
  categories: MarketingTagCategory[]
  onClose: () => void
}) {
  const { toast } = useToast()
  const del = useDeleteMarketingTagValue()
  const add = useCreateMarketingTagValue()
  const [confirm, setConfirm] = useState<{ id: string; value: string; count: number } | null>(null)
  const [newValues, setNewValues] = useState<Record<string, string>>({})

  const addValue = async (categoryKey: string) => {
    const value = (newValues[categoryKey] ?? '').trim()
    if (!value) return
    try {
      await add.mutateAsync({ category_key: categoryKey, value })
      toast({ title: 'Tag value added' })
      setNewValues((s) => ({ ...s, [categoryKey]: '' }))
    } catch (e) {
      toast({
        title: 'Could not add value',
        description:
          (e as { response?: { data?: { error?: string } } })?.response?.data?.error || '',
        variant: 'destructive',
      })
    }
  }

  const remove = async (id: string, value: string) => {
    try {
      await del.mutateAsync({ id })
      toast({ title: 'Tag value deleted' })
    } catch (e) {
      const resp = (e as { response?: { status?: number; data?: { inUseCount?: number } } })
        .response
      if (resp?.status === 409) {
        setConfirm({ id, value, count: resp.data?.inUseCount ?? 0 })
      } else {
        toast({ title: 'Delete failed', variant: 'destructive' })
      }
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col">
        <DialogHeader>
          <DialogTitle>Manage tag values</DialogTitle>
        </DialogHeader>
        <div className="flex-1 space-y-4 overflow-y-auto pr-1">
          {categories.map((c) => (
            <div key={c.key}>
              <h3 className="mb-1 text-sm font-semibold">{c.label}</h3>
              <div className="flex flex-wrap gap-1">
                {c.values.length === 0 && (
                  <span className="text-xs text-muted-foreground">No values.</span>
                )}
                {c.values.map((v) => (
                  <span
                    key={v.id}
                    className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-xs"
                  >
                    {v.value}
                    <button
                      type="button"
                      onClick={() => remove(v.id, v.value)}
                      aria-label={`Delete ${v.value}`}
                      className="text-destructive hover:opacity-70"
                    >
                      <Trash2 className="h-3 w-3" aria-hidden="true" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="mt-1.5 flex gap-1">
                <Input
                  value={newValues[c.key] ?? ''}
                  onChange={(e) => setNewValues((s) => ({ ...s, [c.key]: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addValue(c.key)
                    }
                  }}
                  placeholder={`Add ${c.label} value…`}
                  className="h-8 max-w-xs text-sm"
                  aria-label={`New ${c.label} value`}
                />
                <Button
                  size="sm"
                  className="h-8"
                  onClick={() => addValue(c.key)}
                  aria-label={`Add ${c.label} value`}
                >
                  <Plus className="mr-1 h-3 w-3" aria-hidden="true" />
                  Add
                </Button>
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete tag value in use?</AlertDialogTitle>
            <AlertDialogDescription>
              “{confirm?.value}” is applied to {confirm?.count} asset(s). Deleting it will remove
              the tag from those assets.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (confirm) {
                  await del.mutateAsync({ id: confirm.id, confirm: true })
                  toast({ title: 'Tag value deleted' })
                }
                setConfirm(null)
              }}
            >
              Delete anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  )
}
